import React, { useState, useMemo } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  // MapPin,
  // User,
  // Eye
} from 'lucide-react';
import { Button } from '../../components/shared';
import { useVendorBookings } from '../../services/vendorBookingService';
import { useNavigate } from 'react-router-dom';

const VendorCalendar = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Fetch all bookings for the calendar
  const { data: bookingsResponse, isLoading, error } = useVendorBookings({});
  const bookings = useMemo(() => bookingsResponse?.data?.data || [], [bookingsResponse?.data?.data]);

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayBookings = bookings.filter(booking => {
        const bookingDate = new Date(booking.event_date);
        return bookingDate.toDateString() === date.toDateString();
      });
      
      days.push({
        day,
        date,
        bookings: dayBookings
      });
    }
    
    return days;
  }, [currentMonth, currentYear, bookings, daysInMonth, startingDayOfWeek]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get month name
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      no_show: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || colors.pending;
  };

  const getBookingDotStyle = (dayBookings) => {
    if (!dayBookings.length) return null;
    const hasLive = dayBookings.some((b) => ['confirmed', 'in_progress'].includes(b.status));
    if (hasLive) {
      return { ping: 'bg-purple-400', solid: 'bg-purple-600', title: 'Active booking on this date' };
    }
    return { ping: 'bg-emerald-400', solid: 'bg-emerald-600', title: 'Booking on this date' };
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No Show'
    };
    return labels[status] || 'Pending';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Calendar</h3>
          <p className="text-gray-600">Unable to load your bookings calendar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Booking Calendar</h1>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={goToToday}
                size="sm"
              >
                Today
              </Button>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  onClick={goToPreviousMonth}
                />
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  onClick={goToNextMonth}
                />
              </div>
            </div>
          </div>
          
          {/* Month/Year Display */}
          <div className="mt-4">
            <h2 className="text-3xl font-bold text-gray-900">
              {monthNames[currentMonth]} {currentYear}
            </h2>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Days of week header */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-4 text-center text-sm font-semibold text-gray-700">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((dayData, index) => (
              <div
                key={index}
                className={`relative min-h-[120px] border-r border-b border-gray-200 p-2 overflow-visible ${
                  dayData ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                {dayData && (
                  <>
                    {/* Day number with in-cell event dot on the date */}
                    <div className="mb-2">
                      <div className="relative inline-flex">
                        <span
                          className={`text-sm font-medium ${
                            dayData.date.toDateString() === new Date().toDateString()
                              ? 'text-blue-600 bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center'
                              : 'text-gray-900 w-6 h-6 flex items-center justify-center'
                          }`}
                        >
                          {dayData.day}
                        </span>
                        {(() => {
                          const dot = getBookingDotStyle(dayData.bookings);
                          if (!dot) return null;
                          return (
                            <span
                              className="absolute -top-0.5 -right-1 flex h-2 w-2"
                              title={dot.title}
                              aria-hidden
                            >
                              <span
                                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${dot.ping}`}
                              />
                              <span
                                className={`relative inline-flex h-2 w-2 rounded-full ${dot.solid}`}
                              />
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Bookings for this day */}
                    <div className="space-y-1">
                      {dayData.bookings.slice(0, 3).map((booking, bookingIndex) => (
                        <div
                          key={booking.id}
                          className={`text-xs p-2 rounded border cursor-pointer hover:shadow-sm transition-shadow ${getStatusColor(booking.status)}`}
                          onClick={() => navigate(`/vendor/bookings/${booking.id}`)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span className="font-medium">
                                {formatTime(booking.start_time)}
                              </span>
                            </div>
                            <span className="text-[10px] font-semibold uppercase">
                              {getStatusLabel(booking.status)}
                            </span>
                          </div>
                          <div className="font-mono text-xs font-medium">
                            #{booking.booking_reference}
                          </div>
                          <div className="truncate">
                            {booking.client?.name || 'Unknown Client'}
                          </div>
                        </div>
                      ))}
                      
                      {/* Show more indicator if there are more than 3 bookings */}
                      {dayData.bookings.length > 3 && (
                        <div className="text-xs text-gray-500 text-center py-1">
                          +{dayData.bookings.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Legend</h3>
          <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-purple-600" />
            </span>
            <span className="text-sm text-gray-700">
              Dot on the date — purple blink = confirmed / in progress; green blink = other bookings
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { status: 'pending', label: 'Pending' },
              { status: 'confirmed', label: 'Confirmed' },
              { status: 'in_progress', label: 'In Progress' },
              { status: 'completed', label: 'Completed' },
              { status: 'cancelled', label: 'Cancelled' },
              { status: 'no_show', label: 'No Show' }
            ].map(({ status, label }) => (
              <div key={status} className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded border ${getStatusColor(status)}`}></div>
                <span className="text-sm text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorCalendar;
